import React, { useState, useEffect } from "react";
import Navbar from "../Navbar";
import Sidebar from "./superAdminSidebar";
import Footer from "../Footer";
import { handleAPIRequest } from "../../../api/api";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Swal from "sweetalert2";

const MembershipPlans = () => {
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [formData, setFormData] = useState({
    title: "",
    role_type: "ngo",
    price: "",
    duration: "",
    duration_type: "month",
    description: "",
  });
  const [editMode, setEditMode] = useState(false); // To check if we're editing a plan or creating new one
  const [selectedPlanId, setSelectedPlanId] = useState(null); // Store selected plan for editing
  const [searchTerm, setSearchTerm] = useState(""); // Search term
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [plansPerPage] = useState(5); // Number of plans to display per page
  const token = useSelector((state) => state.auth.token);

  // Fetch all membership plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await handleAPIRequest(
          "get",
          "membership-plans",
          null,
          null,
          null
        );
        setMembershipPlans(response);
        setFilteredPlans(response); // Initially show all plans
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to fetch membership plans.");
      }
    };
    fetchPlans();
  }, [token]);

  // Filter plans based on search term
  useEffect(() => {
    const filtered = membershipPlans.filter(
      (plan) =>
        plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.role_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.price.toString().includes(searchTerm) ||
        plan.duration.toString().includes(searchTerm) ||
        plan.duration_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPlans(filtered);
    setCurrentPage(1); // Reset to the first page on every new search
  }, [searchTerm, membershipPlans]);

  // Handle pagination
  const indexOfLastPlan = currentPage * plansPerPage;
  const indexOfFirstPlan = indexOfLastPlan - plansPerPage;
  const currentPlans = filteredPlans.slice(indexOfFirstPlan, indexOfLastPlan);

  // Open/close sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Open the modal to create or edit a plan
  const openModal = (plan = null) => {
    if (plan) {
      setEditMode(true);
      setSelectedPlanId(plan.id);
      setFormData({
        title: plan.title,
        role_type: plan.role_type,
        price: plan.price,
        duration: plan.duration,
        duration_type: plan.duration_type,
        description: plan.description,
      });
    } else {
      setEditMode(false);
      setFormData({
        title: "",
        role_type: "ngo",
        price: "",
        duration: "",
        duration_type: "month",
        description: "",
      });
    }
    setIsModalOpen(true);
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle submit (Create/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editMode) {
        // Edit the membership plan
        response = await handleAPIRequest(
          "post",
          `membership-plans-edit/${selectedPlanId}`,
          formData,
          null,
          token
        );
        toast.success("Membership plan updated successfully");
      } else {
        // Create a new membership plan
        response = await handleAPIRequest(
          "post",
          "membership-plans",
          formData,
          null,
          token
        );
        toast.success("Membership plan created successfully");
      }
      // Update local state to reflect changes without re-fetching
      if (editMode) {
        setMembershipPlans((prevPlans) =>
          prevPlans.map((plan) =>
            plan.id === selectedPlanId ? { ...plan, ...formData } : plan
          )
        );
      } else {
        setMembershipPlans((prevPlans) => [...prevPlans, response]);
      }
      // Close modal after submission
      setIsModalOpen(false);
      setFormData({
        title: "",
        role_type: "ngo",
        price: "",
        duration: "",
        duration_type: "month",
        description: "",
      });
    } catch (error) {
      toast.error("Error submitting the form");
      console.error(error);
    }
  };

  // Handle delete plan

  const handleDelete = async (id) => {
    // Show confirmation dialog using SweetAlert
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    // Proceed with deletion if confirmed
    if (result.isConfirmed) {
      try {
        await handleAPIRequest(
          "delete",
          `membership-plans/${id}`,
          null,
          null,
          token
        );
        toast.success("Membership plan deleted successfully");

        // Update local state by removing the deleted plan
        setMembershipPlans((prevPlans) =>
          prevPlans.filter((plan) => plan.id !== id)
        );
      } catch (error) {
        toast.error("Error deleting the plan");
        console.error(error);
      }
    } else {
      // Optional: Show a cancellation message or do nothing
      toast.info("Membership plan deletion canceled.");
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Pagination component
  const renderPagination = () => {
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(filteredPlans.length / plansPerPage); i++) {
      pageNumbers.push(i);
    }
    return (
      <div className="flex justify-center mt-4">
        <ul className="flex space-x-2">
          {pageNumbers.map((number) => (
            <li
              key={number}
              className={`px-4 py-2 cursor-pointer ${
                currentPage === number
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => handlePageChange(number)}
            >
              {number}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="flex-1 flex flex-col">
        <Navbar toggleSidebar={toggleSidebar} />
        <main className="flex-grow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-700">
              Membership Plans
            </h2>
            <button
              onClick={() => openModal()}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Add New Plan
            </button>
          </div>
          {/* Search bar */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="Search by title, role type, price, duration or duration type..."
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Title
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Role Type
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Price
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Duration Time
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Duration Type
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Created At
                  </th>
                  <th className="py-3 px-6 text-left text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentPlans.length > 0 ? (
                  currentPlans.map((plan) => (
                    <tr key={plan.id} className="border-b">
                      <td className="py-3 px-6 text-gray-600">{plan.title}</td>
                      <td className="py-3 px-6 text-gray-600">
                        {plan.role_type}
                      </td>
                      <td className="py-3 px-6 text-gray-600">{plan.price}</td>
                      <td className="py-3 px-6 text-gray-600">
                        {plan.duration}{" "}
                      </td>
                      <td className="py-3 px-6 text-gray-600">
                        {" "}
                        {plan.duration_type}
                      </td>
                      <td className="py-3 px-6 text-gray-600">
                        {" "}
                        {new Date(plan.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-6 text-gray-600 flex gap-2">
                        <button
                          onClick={() => openModal(plan)}
                          className="text-blue-500"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(plan.id)}
                          className="text-red-500"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="py-3 px-6 text-center text-gray-500"
                    >
                      No membership plans available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {renderPagination()}
        </main>

        {/* Modal for Create/Edit */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96 max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">
                {editMode ? "Edit" : "Create"} Membership Plan
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Role Type
                  </label>
                  <select
                    name="role_type"
                    value={formData.role_type}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="ngo">NGO</option>
                    <option value="donor">Donor</option>
                    <option value="supplier">Supplier</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Price
                  </label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Duration
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Duration Type
                  </label>
                  <select
                    name="duration_type"
                    value={formData.duration_type}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.description}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setFormData((prevData) => ({
                        ...prevData,
                        description: data,
                      }));
                    }}
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-500 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded"
                  >
                    {editMode ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ToastContainer />
        <Footer />
      </div>
    </div>
  );
};

export default MembershipPlans;
